import "./css/style.scss";
import SwiperCore from "swiper";
import { Navigation, Controller } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import SwiperComponent from "./swiper/Swiper.component";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CampaignType,
  ICONS_DEFAULT_VALUES,
  LayoutDevices,
  LayoutDeviceSettings,
  Offer,
  PlacementStage,
  QueryParams,
  QuerySaveEmail,
  SelectedOfferStorage,
  TemplateOptionSettings,
} from "../../../types/global.types";
import { getTrackingEvent, postTagEmailOffer } from "../../../services/offers/offers.service";
import TemplateSingle from "../single/TemplateSingle.component";
import LayoutEmail from "../../layout/email";
import PreCountAndSelectAll from "../components/PreCountAndSelectAll/PreCountAndSelectAll";
import RedirectionButton from "../components/Redirection/redirectionButton.component";
import TemplateIcon from "../components/Icons/info.component";
import FooterText from "../components/FooterText/FooterText";
import EmailAutomaticTermsBanner from "../components/EmailAutomaticTermsBanner/EmailAutomaticTermsBanner.component";
import { getHeaderEmailAutomaticTermsText } from "../utils/emailAutomaticTerms.utils";
import { EMAIL_REGEX } from "../../../utility/regexp";
import { getStorageItem, updateStorageItem } from "../../../utility/storage";

SwiperCore.use([Navigation, Controller]);

interface Props {
  params: QueryParams;
  layoutSettings: LayoutDeviceSettings;
  placementId: string;
  placementName: string;
  campaignRes: CampaignType;
  selectedOffers: string[];
  singleOffer: Offer | undefined;
  isSave: boolean | undefined;
  isShare: boolean | undefined;
  isEmail: boolean;
  email: string | undefined;
  slot: number;
  isSeen: boolean;
  sessionId: string;
  setSingle: React.Dispatch<React.SetStateAction<Offer | undefined>>;
  setIsEmail: React.Dispatch<React.SetStateAction<boolean>>;
  layoutId: string;
  device: LayoutDevices;
}

const PreviewEmbeddedHorizontalV2 = ({
  layoutSettings,
  params,
  isSave,
  isShare,
  campaignRes,
  placementId,
  placementName,
  selectedOffers,
  singleOffer,
  setSingle,
  setIsEmail,
  isEmail,
  email,
  slot,
  isSeen,
  sessionId,
  layoutId,
  device,
}: Props) => {
  const settingsTemplate = layoutSettings?.template;
  const settings: TemplateOptionSettings | undefined = layoutSettings.settings;
  const swiperParentRef = useRef<HTMLDivElement>(null);
  const [displayOnly] = useState<boolean>(
    campaignRes?.placementSettings[0]?.displayOnly
  );
  const [campaign, setCampaign] = useState<CampaignType | undefined>(
    campaignRes
  );
  const [selectedEmailOfferId, setSelectedEmailOfferId] = useState<string>("");
  const [autoSendingOfferId, setAutoSendingOfferId] = useState<string>("");
  const [preEmail, setPreEmail] = useState<string | undefined>(email);
  const [isSingleSave, setSingleSave] = useState<boolean>(false);
  const [idsEmailSent, setIdsEmailSent] = useState<string[]>([]);
  const [hasOpen, setHasOpen] = useState<boolean>(false);

  const updateCampaignBySingle = (o: Offer) => {
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
        if (offer.id === o.id) {
          return o;
        }
        return offer;
      });

      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
    }
  };

  useEffect(() => {
    window.addEventListener("message", function (event) {
      if (Object.keys(event.data).includes("single")) {
        updateCampaignBySingle(event.data.single);
      }
    });

    return () => {
      window.removeEventListener("message", () => { });
    };
    // eslint-disable-next-line
  }, [campaign]);

  const resolvedEmailForAutoSend = useMemo(() => {
    if (preEmail?.trim()) {
      return preEmail.trim();
    }
    if (email?.trim()) {
      return email.trim();
    }

    const cookieOptions = campaign?.cookieOptions || {
      allowCookies: false,
      allowSessionCookies: false,
      allowPersistentCookies: false,
      expiryDays: "1000",
    };
    return getStorageItem("brswEmail", cookieOptions)?.trim() || "";
  }, [campaign?.cookieOptions, email, preEmail]);

  const canAutoSendEmailFromLabel =
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const canAutoSendEmailFromMainCTA =
    campaign?.campaignGeneralOptions?.autoSendEmail === true &&
    campaign?.campaignGeneralOptions?.manualEmailEntry === false &&
    Boolean(resolvedEmailForAutoSend) &&
    EMAIL_REGEX.test(resolvedEmailForAutoSend);

  const handleAutoSendFromOfferLabel = async (
    offer: Offer,
    slot: number
  ): Promise<boolean> => {
    if (
      offer.offerSettings?.bundleOptions?.isBundled ||
      !resolvedEmailForAutoSend.trim()
    ) {
      return false;
    }

    setAutoSendingOfferId(offer.id);
    const emailParams: QuerySaveEmail = {
      offerid: offer.id,
      email: resolvedEmailForAutoSend,
      offerLink: offer.trackingUrl,
      companyId: campaign?.company_Id || "",
      placementId,
      campaignId: campaign?.id || "",
      templateId: settingsTemplate.identifier,
      slot,
      orderId: offer.orderId,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      layoutId,
    };

    try {
      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId: campaign?.company_Id,
        campaignId: campaign?.id || "",
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        updateStorageItem(
          "brswEmail",
          resolvedEmailForAutoSend,
          campaign?.cookieOptions
        );
        setPreEmail(resolvedEmailForAutoSend);
        setSelectedEmailOfferId("");
        return true;
      }
      return false;
    } finally {
      setAutoSendingOfferId("");
    }
  };

  const handleCloseSingle = (emailSent: boolean, id: string) => {
    emailSent && setIdsEmailSent((prevState) => [...prevState, id]);
    setSingle(undefined);
    setSingleSave(false);
  };

  const checkSelectedOffers = (
    selectedOffers: string[] = [],
    _campaign: CampaignType
  ) => {
    const newOffers: Offer[] = _campaign.offers.map((offer: Offer) => {
      return { ...offer, isAdded: selectedOffers.includes(offer.id) };
    });
    setCampaign(
      (prevState) => prevState && { ...prevState, offers: newOffers }
    );
  };

  const openSingle = (
    offer: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => {
    if (isAutoEmail) {
      const params = { offer, isSave, isAuto: true, slot };
      window.top?.postMessage(
        { event: "emailPopupEmbedded", load: params },
        "*"
      );
    } else {
      window.parent.postMessage(
        {
          event: "openSingleEmbedded",
          load: { offer, isSave, slot, isShare: isShare || false },
        },
        "*"
      );
    }
  };

  const handleAdd = (offer: Offer, value: boolean, slot: number) => {
    setSelectedOfferStorage({
      id: offer.id,
      link: offer.trackingUrl,
      active: value,
      layoutId: layoutId || "",
    });
    if (campaign) {
      const newOffers: Offer[] = campaign.offers.map((_offer: Offer) => {
        if (_offer.id === offer.id) {
          _offer.isAdded = value;
        }
        return _offer;
      });
      setCampaign(
        (prevState) => prevState && { ...prevState, offers: newOffers }
      );
    } else {
      const _single = { ...singleOffer, isAdded: value };
      setSingle((prevState) => prevState && { ...prevState, isAdded: value });
      window.top?.postMessage(
        { event: "singleChange", offer: _single, slot },
        "*"
      );
    }

    value &&
      campaign &&
      getTrackingEvent({
        event: "onselectoffer",
        offerId: offer.id,
        retailerId: campaign.company_Id,
        campaignId: campaign.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });
  };

  const handleSelectAll = () => {
    if (!campaign) {
      return;
    }

    const selectedLength = campaign.offers.filter(
      (o) => selectedOffers.includes(o.id) && !o.tagBlocked?.isBlocked
    ).length;
    const offerLength = campaign?.offers.filter(
      (o) => !o.tagBlocked?.isBlocked
    ).length;
    const shouldRemove = selectedLength === offerLength;

    // Batch update all offers at once instead of individual handleAdd calls
    const offersToUpdate = campaign.offers.filter(
      (offer) =>
        !offer.tagBlocked?.isBlocked &&
        (shouldRemove ? offer.isAdded : !offer.isAdded)
    );

    if (offersToUpdate.length === 0) {
      return;
    }

    // Update campaign state with all changes at once
    const newOffers: Offer[] = campaign.offers.map((offer: Offer) => {
      const shouldUpdate = offersToUpdate.some((o) => o.id === offer.id);
      if (shouldUpdate) {
        return { ...offer, isAdded: !shouldRemove };
      }
      return offer;
    });

    setCampaign(
      (prevState) => prevState && { ...prevState, offers: newOffers }
    );

    // Batch send storage updates for all offers using setSelectedOffersStorage
    const storageUpdates: SelectedOfferStorage[] = offersToUpdate.map(
      (offer) => ({
        id: offer.id,
        link: offer.trackingUrl,
        active: !shouldRemove,
        layoutId: layoutId || "",
      })
    );

    // Send all storage updates at once using the new batch method
    setSelectedOffersStorage(storageUpdates);

    // Batch tracking events for selected offers (only when adding, not removing)
    if (!shouldRemove && campaign) {
      offersToUpdate.forEach((offer) => {
        getTrackingEvent({
          event: "onselectoffer",
          offerId: offer.id,
          retailerId: campaign.company_Id,
          campaignId: campaign.id,
          placementId,
          placementName,
          tid: settingsTemplate.identifier,
          slot: offer.slot,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          pltid: layoutId,
        });
      });
    }
  };

  const setSelectedOfferStorage = (storage: SelectedOfferStorage) => {
    window.top?.postMessage({ event: "setSelectedOffer", load: storage }, "*");
  };

  const setSelectedOffersStorage = (storage: SelectedOfferStorage[]) => {
    window.top?.postMessage({ event: "setSelectedOffers", load: storage }, "*");
  };

  useEffect(() => {
    if (campaignRes) {
      setCampaign(campaignRes);
      if (campaignRes && !singleOffer) {
        // (hasOpen) && getTrackingEvent({ event: 'onmainbuttonclick', retailerId: campaignRes?.company_Id, campaignId: campaignRes.id, placementId, placementName, tid: settingsTemplate.identifier });

        !hasOpen &&
          getTrackingEvent({
            event: "ontemplateopen",
            retailerId: campaignRes.company_Id,
            campaignId: campaignRes.id,
            placementId,
            placementName,
            tid: settingsTemplate.identifier,
            pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
            pltid: layoutId,
          });
      }
      setHasOpen(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    checkSelectedOffers(selectedOffers, campaignRes);
  }, [selectedOffers, campaignRes]);

  useEffect(() => {
    document.body.classList.add("no-scroll");
  }, []);

  const svgAnimationKey = [
    settings?.mainLayout?.iconAnimation,
    settings?.mainLayout?.iconAnimationType,
    settings?.mainLayout?.iconAnimationRepeat,
    isSeen,
  ].join("-");

  const svgAnimationStyle = settings?.mainLayout?.iconAnimation
    ? {
      animation: `${settings?.mainLayout?.iconAnimationType} 1s linear`,
      animationIterationCount: settings?.mainLayout?.iconAnimationRepeat,
      display: "inline-flex",
    }
    : {};

  const resolvedEmailForBanner =
    preEmail || email || params?.contactDetails?.[0]?.email;
  const headerEmailAutomaticTermsText = getHeaderEmailAutomaticTermsText(
    settings?.mainLayout,
    campaign?.campaignGeneralOptions,
    settingsTemplate.identifier
  );

  return (
    <>
      {singleOffer && (
        <>
          {isEmail ? (
            <LayoutEmail
              settings={layoutSettings.popup}
              templateSettings={layoutSettings.settings}
              emailPopupOffer={singleOffer}
              isEmbedded={true}
              setIsEmail={setIsEmail}
              setSingle={setSingle}
              prePopulateEmail={preEmail}
              campaignObj={campaignRes}
              campaignId={campaignRes.id}
              placementId={placementId}
              placementName={placementName}
              slot={slot}
              layoutId={layoutId}
            ></LayoutEmail>
          ) : (
            <>
              {campaign?.id && settings && (
                <TemplateSingle
                  slot={slot}
                  settings={settings}
                  isInline={true}
                  offer={singleOffer}
                  isSave={isSave || isSingleSave}
                  isShare={isShare || false}
                  placementId={placementId}
                  placementName={placementName}
                  settingsTemplate={settingsTemplate}
                  campaignObj={campaign}
                  displayOnly={displayOnly}
                  campaignId={campaign?.id}
                  companyId={campaign.company_Id}
                  updateCampaign={handleAdd}
                  setPreEmail={setPreEmail}
                  preEmail={preEmail || email}
                  closeSignle={handleCloseSingle}
                  layoutId={layoutId}
                />
              )}
            </>
          )}
        </>
      )}

      <div
        className={`inline-horizontal-wrapper  ${settings?.mainLayout?.align} ${singleOffer || isEmail ? "hidden" : "displayed"
          }`}
        role="region"
        aria-label="Special offers and promotions"
        style={{
          padding: `0 ${settings?.embeddedElement?.margin || "0"}${settings?.embeddedElement?.marginType || "%"
            }`,
        }}
      >
        <div
          className={`inline-horizontal-popup`}
          style={{
            width: settings?.embeddedElement?.fullWidth
              ? "100%"
              : settings?.embeddedElement?.width,
            backgroundColor: settings?.mainLayout.backgroundColor,
            border: "1px solid",
            borderColor:
              settings?.mainLayout?.borderColor ||
              settings?.mainLayout.backgroundColor,
            borderRadius: `${settings?.mainLayout?.borderRadius}px`,

            fontFamily: settings?.mainLayout.fontFamily,
            color: settings?.mainLayout.textColor,
            height: `${settings?.embeddedElement?.height}px`,
            padding: `${settings?.mainLayout?.paddingTopBottom + "px" || "0"} ${settings?.mainLayout?.paddingLeftRight + "px" || "0"
              }`,
          }}
        >
          <div className={`inline-horizontal-title`}>
            {settings?.mainLayout?.headerIcon === "gift" && (
              <>
                <div
                  className="inline-horizontal-title-icon"
                  key={svgAnimationKey}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={svgAnimationStyle}
                  >
                    <path
                      d="M17.2 10V19H2.8V10M10 19V5.5M10 5.5H5.95C5.35326 5.5 4.78097 5.26295 4.35901 4.84099C3.93705 4.41903 3.7 3.84674 3.7 3.25C3.7 2.65326 3.93705 2.08097 4.35901 1.65901C4.78097 1.23705 5.35326 1 5.95 1C9.1 1 10 5.5 10 5.5ZM10 5.5H14.05C14.6467 5.5 15.219 5.26295 15.641 4.84099C16.0629 4.41903 16.3 3.84674 16.3 3.25C16.3 2.65326 16.0629 2.08097 15.641 1.65901C15.219 1.23705 14.6467 1 14.05 1C10.9 1 10 5.5 10 5.5ZM1 5.5H19V10H1V5.5Z"
                      stroke={settings?.mainLayout.headerTextColor}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </>
            )}

            {settings?.mainLayout?.headerIcon === "lock" && (
              <>
                <div
                  className="inline-horizontal-title-icon"
                  key={svgAnimationKey}
                >
                  {displayOnly ? (
                    <svg
                      width="42"
                      height="47"
                      viewBox="0 0 42 47"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={svgAnimationStyle}
                    >
                      <path
                        d="M9.88889 21.25V12.25C9.88889 9.26631 11.0595 6.40483 13.1433 4.29505C15.227 2.18526 18.0532 1 21 1C23.9469 1 26.773 2.18526 28.8567 4.29505C30.9405 6.40483 32.1111 9.26631 32.1111 12.25V21.25M5.44444 21.25H36.5556C39.0102 21.25 41 23.2647 41 25.75V41.5C41 43.9853 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9853 1 41.5V25.75C1 23.2647 2.98985 21.25 5.44444 21.25Z"
                        stroke={settings?.mainLayout.headerTextColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="42"
                      height="47"
                      viewBox="0 0 42 47"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={svgAnimationStyle}
                    >
                      <path
                        d="M9.88889 21.2554V12.258C9.88613 9.46896 10.9073 6.77834 12.7542 4.7085C14.601 2.63865 17.1419 1.33726 19.8834 1.05697C22.6248 0.776667 25.3714 1.53746 27.5899 3.19164C29.8084 4.84583 31.3406 7.2754 31.8889 10.0087M5.44444 21.2572H36.5556C39.0102 21.2572 41 23.2714 41 25.7559V41.5013C41 43.9859 39.0102 46 36.5556 46H5.44444C2.98985 46 1 43.9859 1 41.5013V25.7559C1 23.2714 2.98985 21.2572 5.44444 21.2572Z"
                        stroke={settings?.mainLayout.headerTextColor}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </>
            )}

            {settings?.mainLayout?.headerIcon === "custom" &&
              settings?.mainLayout?.headerCustomIcon && (
                <div
                  className="inline-horizontal-title-icon"
                  key={svgAnimationKey}
                  style={svgAnimationStyle}
                  dangerouslySetInnerHTML={{
                    __html: settings.mainLayout.headerCustomIcon,
                  }}
                />
              )}

            <span
              className={`inline-horizontal-title-label`}
              style={{
                fontSize: settings?.mainLayout.headerFontSize + "px",
                fontWeight: settings?.mainLayout.headerFontWeight,
                color: settings?.mainLayout.headerTextColor,
              }}
              tabIndex={0}
              role="contentinfo"
              aria-label={settings?.mainLayout.headerText || ""}
            >
              {settings?.mainLayout.headerText || ""}
            </span>
          </div>

          <div
            className={`inline-horizontal-description`}
            style={{
              fontSize: settings?.mainLayout.subHeaderFontSize + "px",
              fontWeight: settings?.mainLayout.subHeaderFontWeight,
              color: settings?.mainLayout.subHeaderTextColor,
            }}
            tabIndex={0}
            role="contentinfo"
            aria-label={settings?.mainLayout.subHeaderText || ""}
          >
            {settings?.mainLayout.subHeaderText || ""}
          </div>

          <EmailAutomaticTermsBanner
            campaignGeneralOptions={campaign?.campaignGeneralOptions}
            cookieOptions={campaign?.cookieOptions}
            text={headerEmailAutomaticTermsText}
            email={resolvedEmailForBanner}
            icon={settings?.dealLayout?.iconInfo?.value}
            onEmailChange={setPreEmail}
            mobile={device === LayoutDevices.mobile}
          />

          {settings?.mainLayout?.showQualifiedHeading && (
            <div className="inline-horizontal-qualified">
              <span
                style={{
                  color: settings?.mainLayout?.highlightColor || "#3ab78f",
                }}
                tabIndex={0}
                role="contentinfo"
                aria-label={
                  settings?.mainLayout?.qualifiedHeadingText || "Qualified"
                }
              >
                {settings?.mainLayout?.qualifiedHeadingText || "Qualified"}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="8"
                  fill={settings?.mainLayout?.highlightColor || "#3ab78f"}
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.8156 5.15496C11.9879 5.33395 11.9879 5.62417 11.8156 5.80317L6.96038 10.8451C6.78801 11.0241 6.50854 11.0241 6.33617 10.8451L4.12928 8.5533C3.95691 8.3743 3.95691 8.08409 4.12928 7.90509C4.30165 7.72609 4.58111 7.72609 4.75348 7.90509L6.64828 9.87276L11.1913 5.15496C11.3637 4.97596 11.6432 4.97596 11.8156 5.15496Z"
                  fill="white"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {displayOnly &&
            campaign?.campaignGeneralOptions?.showAddToOrder &&
            settings?.otherSettings?.showSelectAll &&
            settings?.otherSettings?.showCounter && (
              <PreCountAndSelectAll
                offerLength={
                  campaign?.offers.filter((o) => !o.tagBlocked?.isBlocked)
                    .length
                }
                countText={settings?.otherSettings?.counterText}
                selectText={settings?.otherSettings?.selectedText}
                showCheckboxSelect={
                  settings?.otherSettings?.showCheckboxSelect || false
                }
                showSelectAll={settings?.otherSettings?.showSelectAll || false}
                showCount={settings?.otherSettings?.showCounter || false}
                noOfSelected={
                  campaign?.offers
                    .filter((o) => selectedOffers.includes(o.id))
                    .filter((o) => !o.tagBlocked?.isBlocked).length
                }
                handleSelectAll={handleSelectAll}
              />
            )}

          <div className={`inline-horizontal-swiper`} ref={swiperParentRef}>
            {campaign && campaign?.offers.length > 0 && settings && (
              <SwiperComponent
                sessionId={sessionId}
                isSeen={isSeen}
                settings={settings}
                campaign={campaign}
                emailSentIds={idsEmailSent}
                settingsTemplate={settingsTemplate}
                displayOnly={displayOnly}
                campaignId={campaign.id}
                placementId={placementId}
                placementName={placementName}
                openSingle={openSingle}
                onAdd={handleAdd}
                layoutId={layoutId}
                canAutoSendEmailFromLabel={canAutoSendEmailFromLabel}
                canAutoSendEmailFromMainCTA={canAutoSendEmailFromMainCTA}
                onAutoSendEmailFromLabel={handleAutoSendFromOfferLabel}
                autoSendingOfferId={autoSendingOfferId}
              ></SwiperComponent>
            )}
          </div>

          <RedirectionButton settings={settings} displayOnly={displayOnly} />

          <div className="inline-horizontal-footer" style={{ marginTop: 0 }}>
            {displayOnly ? (
              <>
                {campaign?.campaignGeneralOptions.emailCaptureOnOffers && (
                  <>
                    <div role="img" aria-label="Information">
                      <TemplateIcon
                        icon={
                          settings?.dealLayout?.iconEmail?.value ||
                          ICONS_DEFAULT_VALUES.iconEmail
                        }
                        color={settings?.mainLayout.textColor || "#888888"}
                      />
                    </div>

                    <FooterText
                      displayOnly={displayOnly}
                      footerText={settings?.mainLayout.footerText}
                      mobile={device === LayoutDevices.mobile}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <div role="img" aria-label="Information">
                  <TemplateIcon
                    icon={
                      settings?.dealLayout?.iconInfo?.value ||
                      ICONS_DEFAULT_VALUES.iconInfo
                    }
                    color={settings?.mainLayout.textColor || "#888888"}
                  />
                </div>

                <FooterText
                  displayOnly={displayOnly}
                  footerText={settings?.mainLayout.footerText}
                  mobile={device === LayoutDevices.mobile}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PreviewEmbeddedHorizontalV2;
